/**
 * Shared poll loop for BOTH import flows (photo/OCR — Steg 5 slice 1 — and
 * url — Steg 5 slice 2): a `POST .../import/photo` or `POST .../import/url`
 * creates a job (202 + `jobId`) whose extraction runs in the background; the
 * client polls the SOURCE-AGNOSTIC `GET .../import/jobs/{jobId}` route until
 * it settles. One implementation so `PhotoImportButton` and `UrlImportForm`
 * share EXACTLY the same polling semantics (race-safety, transient-error
 * tolerance, fail-fast on 404, a client-side timeout) instead of drifting.
 *
 * Race-safety: the CALLER owns cancellation via `isActive()` (checked before
 * every network call, and again right after it settles) — typically a
 * `mountedRef.current && runIdRef.current === runId` check, so an unmount or
 * a newer pick/submit never lets a stale loop resolve/reject into stale
 * state. A cancelled poll rejects with `ImportPollCancelledError`, which
 * callers should treat as a silent no-op (never a user-facing error).
 *
 * Error mapping:
 *  - `UnauthorizedError` / `NotFoundError` (from the underlying `getImportJob`
 *    call) propagate AS-IS and end the loop immediately — no attempts are
 *    burned retrying a 401, or a job that will never resolve (unknown / not
 *    ours / vanished).
 *  - Any OTHER error on a single poll (a network blip, a transient 5xx) is
 *    treated as transient and does NOT abort the loop — it just counts
 *    toward the attempt cap.
 *  - `status: "failed"` (or a malformed `"done"` with no `draft` — the
 *    contract guarantees one, but never trust it blindly) rejects with
 *    `ImportJobFailedError`, carrying the job's own short Norwegian `error`
 *    if the server provided one. The caller supplies its own generic
 *    fallback copy, since "prøv et tydeligere bilde" only makes sense for a
 *    photo, not a url. `ImportJobFailedError` also carries the job's
 *    `errorCode`/`errorWorkaround` when present (video-import, Slice 4) — a
 *    photo/url job's failure never sets these, so they're simply `undefined`
 *    there.
 *  - Exhausting `maxAttempts` still `"pending"` rejects with
 *    `ImportPollTimeoutError`.
 *
 * `onProgress` (video-import, Slice 4): an optional callback invoked with the
 * raw status on every poll that's STILL `"pending"` (never on the terminal
 * `"done"`/`"failed"` poll — the caller gets the final result via the
 * resolved/rejected promise instead). Lets a caller render a video job's
 * advisory `phase` (e.g. "Henter video …") while it waits. A photo/url caller
 * simply never passes it — fully backward-compatible.
 *
 * `pollPdfImportJob` (pdf-import, Slice 3b): a SIBLING export, not a
 * parameter on `pollImportJob` — a pdf job's `done` status carries
 * `importedRecipeIds`/`importedCount` INSTEAD OF `draft` (see
 * `PhotoImportJobStatus`'s docs), so it needs its own return type. Both
 * functions share the exact same delay/attempt/isActive/error-mapping loop
 * via the private `pollJobUntilDone` below — only what counts as "the job is
 * done, extract the result" differs — so the two never drift apart.
 */

import type { PhotoImportJobStatus, RecipeImportDraft } from "@tjommi/shared";

import { NotFoundError, UnauthorizedError, getImportJob } from "../api";

/** Poll every ~2s by default — frequent enough to feel responsive without
 * hammering the API. */
export const DEFAULT_POLL_INTERVAL_MS = 2000;

/** ~90 attempts * 2s = ~180s: a client-side cap comfortably under the
 * slowest observed extraction (photo import's Gemini vision call, ~198s
 * worst case), so the client gives up with a clear message rather than
 * polling forever. */
export const DEFAULT_MAX_POLL_ATTEMPTS = 90;

/** ~450 attempts * 2s = ~900s = 15min: a pdf job can span up to
 * `PDF_MAX_PAGES` (150) pages, chunked into ~21 sequential Gemini calls —
 * up to ~10min worst-case backend runtime — so `DEFAULT_MAX_POLL_ATTEMPTS`
 * (3min) was WAY too small and made large-but-successful imports look like a
 * false failure to the user (the job kept running server-side after the
 * client gave up). This budget comfortably covers the worst case with
 * headroom. Used as `PdfImportButton`'s default `maxPollAttempts` — even so,
 * a client-side timeout at THIS cap must still be treated as "probably still
 * running", never a hard failure (see `ImportPollTimeoutError` and
 * `PdfImportButton`'s docs) — the server has no cap of its own. */
export const PDF_IMPORT_MAX_POLL_ATTEMPTS = 450;

export class ImportJobFailedError extends Error {
  /** The job's own short, Norwegian, user-facing reason — absent if the
   * server didn't provide one (the caller falls back to its own generic
   * copy). */
  readonly serverMessage?: string;
  /** Structured failure-taxonomy code (e.g. `LOGIN_REQUIRED`,
   * `QUOTA_EXCEEDED`) — video-import only; `undefined`/`null` for a photo/url
   * job's failure. */
  readonly errorCode?: string | null;
  /** A short, actionable next step for the user, companion to `errorCode` —
   * video-import only; `undefined`/`null` for a photo/url job's failure. */
  readonly errorWorkaround?: string | null;

  constructor(serverMessage?: string, errorCode?: string | null, errorWorkaround?: string | null) {
    super(serverMessage ?? "Import job failed.");
    this.name = "ImportJobFailedError";
    this.serverMessage = serverMessage;
    this.errorCode = errorCode;
    this.errorWorkaround = errorWorkaround;
  }
}

/**
 * Thrown when `maxAttempts` is exhausted while still `"pending"`. For
 * photo/url/video this genuinely means "something is stuck" — the caller
 * shows an error. For pdf-import specifically, the server has NO attempt
 * cap of its own: exhausting even the raised `PDF_IMPORT_MAX_POLL_ATTEMPTS`
 * budget does NOT mean the job failed, only that the CLIENT gave up
 * watching — the job is very likely still running and will still land its
 * recipes. `PdfImportButton` must NOT render this as a red error or offer a
 * retry (retrying would re-run the whole import and double-import every
 * recipe the still-running job eventually lands) — it shows a neutral
 * "continues in the background" notice instead.
 */
export class ImportPollTimeoutError extends Error {
  constructor() {
    super("Import polling timed out.");
    this.name = "ImportPollTimeoutError";
  }
}

/** Thrown when `isActive()` goes false mid-poll (unmount / a newer run
 * started). Callers should treat this as a silent no-op, not an error. */
export class ImportPollCancelledError extends Error {
  constructor() {
    super("Import polling was cancelled.");
    this.name = "ImportPollCancelledError";
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface PollImportJobOptions {
  /** How often to poll, in ms. Default `DEFAULT_POLL_INTERVAL_MS` (injectable for tests). */
  intervalMs?: number;
  /** How many polls before giving up. Default `DEFAULT_MAX_POLL_ATTEMPTS`. */
  maxAttempts?: number;
  /** Checked before AND after every network call; return `false` to cancel. Defaults to always-active. */
  isActive?: () => boolean;
  /** Invoked with the raw status on every poll that's still `"pending"` —
   * lets the caller render advisory progress (e.g. a video job's `phase`).
   * Never called for the terminal `"done"`/`"failed"` poll. Optional;
   * photo/url callers simply don't pass it. */
  onProgress?: (status: PhotoImportJobStatus) => void;
}

/**
 * Shared loop: poll `getImportJob` until it settles, calling `extractDone` on
 * every `"done"` status to pull out the caller-specific result (the
 * `RecipeImportDraft` for photo/url/video, or `{ importedRecipeIds,
 * importedCount }` for pdf). `extractDone` returning `undefined` is treated
 * defensively as a malformed "done" (the contract guarantees the field it
 * looks for, but never trust it blindly) — same as a failure, rather than
 * hang. All other semantics (race-safety, transient-error tolerance,
 * fail-fast on 401/404, the attempt cap, `onProgress`) live here ONCE so
 * `pollImportJob`/`pollPdfImportJob` can't drift apart.
 */
async function pollJobUntilDone<T>(
  cookbookId: string,
  jobId: string,
  accessToken: string,
  options: PollImportJobOptions,
  extractDone: (status: PhotoImportJobStatus) => T | undefined,
): Promise<T> {
  const {
    intervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxAttempts = DEFAULT_MAX_POLL_ATTEMPTS,
    isActive = () => true,
    onProgress,
  } = options;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await delay(intervalMs);
    if (!isActive()) throw new ImportPollCancelledError();

    let status;
    try {
      status = await getImportJob(cookbookId, jobId, accessToken);
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err;
      if (err instanceof NotFoundError) throw err;
      // Transient network/parse blip on a single poll — keep going.
      continue;
    }
    if (!isActive()) throw new ImportPollCancelledError();

    if (status.status === "done") {
      const result = extractDone(status);
      if (result !== undefined) return result;
      // Defensive: the contract guarantees the relevant field when `status`
      // is "done" — treat a malformed response the same as a failure rather
      // than hang.
      throw new ImportJobFailedError();
    }
    if (status.status === "failed") {
      throw new ImportJobFailedError(status.error, status.errorCode, status.errorWorkaround);
    }
    // "pending" — keep polling.
    onProgress?.(status);
  }
  throw new ImportPollTimeoutError();
}

/**
 * Poll an import job (photo, url, or video — the route is source-agnostic)
 * until it settles. Resolves with the finished draft, or rejects with one of
 * the typed errors above (or `UnauthorizedError`/`NotFoundError` re-thrown
 * as-is from `getImportJob`). NOT for a pdf job — use `pollPdfImportJob`.
 */
export async function pollImportJob(
  cookbookId: string,
  jobId: string,
  accessToken: string,
  options: PollImportJobOptions = {},
): Promise<RecipeImportDraft> {
  return pollJobUntilDone(cookbookId, jobId, accessToken, options, (status) => status.draft);
}

/** The result of a settled pdf-import job — see `PhotoImportJobStatus` docs. */
export interface PdfImportResult {
  importedRecipeIds: string[];
  importedCount: number;
}

/**
 * Poll a pdf-import job (pdf-import, Slice 3b) until it settles. Resolves
 * with the ids/count of the recipes it PERSISTED directly (never a draft —
 * see `PhotoImportJobStatus`'s docs), or rejects with one of the typed errors
 * above. Shares the exact same polling semantics as `pollImportJob` via
 * `pollJobUntilDone`.
 */
export async function pollPdfImportJob(
  cookbookId: string,
  jobId: string,
  accessToken: string,
  options: PollImportJobOptions = {},
): Promise<PdfImportResult> {
  return pollJobUntilDone(cookbookId, jobId, accessToken, options, (status) =>
    status.importedRecipeIds
      ? {
          importedRecipeIds: status.importedRecipeIds,
          importedCount: status.importedCount ?? status.importedRecipeIds.length,
        }
      : undefined,
  );
}
